// frontend/pages/Customers.jsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, EnvelopeIcon, PhoneIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { customerService } from '../services/api';
import DataTable from '../components/Common/DataTable';
import Modal from '../components/Common/Modal';
import CustomerForm from '../components/Customers/CustomerForm';
import CustomerDetails from '../components/Customers/CustomerDetails';
import { toast } from 'react-hot-toast';

export default function Customers() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [search, setSearch] = useState('');

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['customers', search],
    queryFn: () => customerService.getCustomers({ search, limit: 100 })
  });

  const sendSMSMutation = useMutation({
    mutationFn: ({ customerId, message }) => customerService.sendSMS(customerId, message),
    onSuccess: () => {
      toast.success('SMS sent successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to send SMS');
    }
  });

  const columns = [
    { key: 'name', header: 'Customer Name', sortable: true },
    { key: 'phone', header: 'Phone', sortable: true },
    { key: 'email', header: 'Email', sortable: true },
    { key: 'current_balance', header: 'Balance', sortable: true, format: 'currency' },
    { 
      key: 'total_purchases', 
      header: 'Lifetime Value', 
      sortable: true, 
      format: 'currency',
      className: 'font-semibold text-green-600'
    },
    { key: 'last_purchase_date', header: 'Last Purchase', sortable: true, format: 'date' }
  ];

  const handleViewCustomer = (customer) => {
    setSelectedCustomer(customer);
    setShowDetailsModal(true);
  };

  const handleSendSMS = (customer) => {
    const message = prompt('Enter SMS message:', `Dear ${customer.name}, we have a special offer for you!`);
    if (message) {
      sendSMSMutation.mutate({ customerId: customer.id, message });
    }
  };

  const actions = [
    {
      label: 'View',
      onClick: handleViewCustomer,
      icon: 'eye'
    },
    {
      label: 'Send SMS',
      onClick: handleSendSMS,
      icon: 'chat'
    },
    {
      label: 'View Transactions',
      onClick: (row) => console.log('View transactions', row),
      icon: 'document'
    }
  ];

  // ✅ Helper function to format currency safely
  const formatCurrency = (value) => {
    const num = typeof value === 'number' ? value : parseFloat(value) || 0;
    return num.toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // ✅ Safely get summary values
  const totalCustomers = data?.total || 0;
  const totalBalance = data?.totalBalance ? (typeof data.totalBalance === 'number' ? data.totalBalance : parseFloat(data.totalBalance) || 0) : 0;
  const averageValue = data?.averageValue ? (typeof data.averageValue === 'number' ? data.averageValue : parseFloat(data.averageValue) || 0) : 0;
  const activeCount = data?.activeCount || 0;

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
          <p className="text-gray-600">Manage customer information and balances</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Customer
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">Total Customers</div>
          <div className="text-2xl font-bold">{totalCustomers.toLocaleString()}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">Total Outstanding Balance</div>
          <div className="text-2xl font-bold text-red-600">
            KES {formatCurrency(totalBalance)}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">Average Customer Value</div>
          <div className="text-2xl font-bold text-green-600">
            KES {formatCurrency(averageValue)}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">Active Customers (30 days)</div>
          <div className="text-2xl font-bold">{activeCount.toLocaleString()}</div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <input
          type="text"
          placeholder="Search customers by name, phone, or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={data?.data || []}
        loading={isLoading}
        actions={actions}
      />

      {/* Create Customer Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Add New Customer"
      >
        <CustomerForm onSuccess={() => {
          setShowCreateModal(false);
          queryClient.invalidateQueries(['customers']);
        }} />
      </Modal>

      {/* Customer Details Modal */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title="Customer Details"
        size="large"
      >
        {selectedCustomer && (
          <CustomerDetails 
            customer={selectedCustomer}
            onClose={() => setShowDetailsModal(false)}
            onUpdate={() => {
              queryClient.invalidateQueries(['customers']);
              setShowDetailsModal(false);
            }}
          />
        )}
      </Modal>
    </div>
  );
}